import { useQuery } from 'wasp/client/operations';
import { getOrganizationsByLocationSlug } from 'wasp/client/operations';
import {
  Building2,
  MapPin,
  Search,
  ChevronRight,
  Globe,
  X,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveOrgLocation } from './orgLocationResolver';
import { OrganizationDetailListItem } from './OrganizationDetailListItem';
import { useResponsiveLayout } from '../../components/ui/enhanced-page-layout';

export default function LocationOrganizationsPage({ defaultSlug }: { defaultSlug?: string } = {}) {
  const { selectedLocation, locations, setSelectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { slug: urlSlug } = useParams<{ slug: string }>();

  const handleOrgClick = useCallback((org: any) => {
    const match = resolveOrgLocation(org, locations);
    if (match) setSelectedLocation(match as any);
    navigate(`/${org.slug}/information-desk`);
  }, [locations, setSelectedLocation, navigate]);

  const [isCompact, setIsCompact] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  // Accumulated orgs across all loaded pages (infinite scroll)
  const accumulatedOrgsRef = useRef<any[]>([]);
  const [accumulatedOrgs, setAccumulatedOrgs] = useState<any[]>([]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  // Prefer URL slug, then defaultSlug prop (for the /organizations top-level route), then selected location.
  const locationSlug = urlSlug ?? defaultSlug ?? selectedLocation?.locationCode ?? selectedLocation?.slug ?? null;
  const isAllMode = locationSlug === 'CA-ALL';

  useEffect(() => {
    setPage(1);
    accumulatedOrgsRef.current = [];
    setAccumulatedOrgs([]);
    isFetchingRef.current = false;
  }, [selectedLocation?.id, locationSlug]);

  const { viewMode, setViewMode, shouldCollapseSidebar, hideViewSwitcher } =
    useResponsiveLayout('table');

  const { data: dataRaw, isLoading, error } = useQuery(
    getOrganizationsByLocationSlug,
    { slug: locationSlug!, page: isAllMode && isCompact ? page : undefined, isMobile: isCompact },
    { enabled: !!locationSlug }
  );

  // Append newly fetched page into the accumulator (infinite scroll)
  useEffect(() => {
    if (!isAllMode || !isCompact || !dataRaw) return;
    const incoming = (dataRaw as any)?.organizations ?? [];
    if (incoming.length === 0) return;
    const currentIds = new Set(accumulatedOrgsRef.current.map((o: any) => o.id));
    const newItems = incoming.filter((o: any) => !currentIds.has(o.id));
    if (newItems.length === 0) return;
    accumulatedOrgsRef.current = [...accumulatedOrgsRef.current, ...newItems];
    setAccumulatedOrgs([...accumulatedOrgsRef.current]);
    isFetchingRef.current = false;
  }, [dataRaw, isAllMode, isCompact]);

  // IntersectionObserver: load next page when sentinel enters viewport
  useEffect(() => {
    if (!isAllMode || !isCompact) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        const tp = (dataRaw as any)?.totalPages ?? 1;
        const cp = (dataRaw as any)?.currentPage ?? page;
        if (cp < tp && !isFetchingRef.current && !isLoading) {
          isFetchingRef.current = true;
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isAllMode, isCompact, dataRaw, isLoading, page]);
  const data = dataRaw as any;

  const location = data?.location;
  // In compact CA-ALL mode use accumulated list; otherwise use the single response
  const allOrganizations: any[] = isAllMode && isCompact
    ? accumulatedOrgs
    : data?.organizations ?? [];
  const mode: 'national' | 'location' | 'all' = data?.mode ?? 'location';
  const totalCount: number = data?.totalCount ?? 0;
  const totalPages: number = data?.totalPages ?? 1;
  const currentPage: number = data?.currentPage ?? page;
  const hasMorePages = isAllMode && isCompact && currentPage < totalPages;

  const filtered = useMemo(() => {
    return allOrganizations.filter((o: any) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const inName = o.name.toLowerCase().includes(q);
        const inDesc = o.description?.toLowerCase().includes(q);
        if (!inName && !inDesc) return false;
      }
      return true;
    });
  }, [allOrganizations, searchTerm]);

  const pageTitle = mode === 'national'
    ? 'National Organizations'
    : mode === 'all'
      ? 'All Organizations'
      : selectedLocation
        ? `Organizations — ${selectedLocation.name}`
        : 'Organizations';

  const pageSubtitle = isLoading
    ? ''
    : mode === 'national'
      ? `${filtered.length} national organization${filtered.length !== 1 ? 's' : ''}`
      : mode === 'all'
        ? filtered.length < totalCount
          ? `Showing ${filtered.length} of ${totalCount} organizations`
          : `All ${totalCount} organizations`
        : `${filtered.length} organization${filtered.length !== 1 ? 's' : ''} in ${selectedLocation?.name ?? ''}`;

  const hasAnyFilter = !!searchTerm;

  const clearAll = () => {
    setSearchTerm('');
    setPage(1);
  };

  // ── MOBILE UI ──────────────────────────────────────────────────────────────
  if (isCompact) {
    return (
      <DashboardLayout
        forceSidebarCollapse={shouldCollapseSidebar}
        showViewSwitcher={false}
        activeView={viewMode}
        onViewChange={setViewMode}
      >
        <div className="w-full flex flex-col h-full">

          {/* Sticky filter bar */}
          <div className="sticky top-0 z-10 bg-content-bg border-b border-border">
            <div className="px-3 py-2.5 flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <h1 className="text-base font-semibold text-content-text truncate">{pageTitle}</h1>
                {!isLoading && filtered.length > 0 && (
                  <span className="text-xs text-content-text-muted flex-shrink-0">
                    {filtered.length}{filtered.length !== allOrganizations.length ? ` of ${allOrganizations.length}` : ''}
                  </span>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-text-muted" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              {hasAnyFilter && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
                    >
                      "{searchTerm}" <X className="w-3 h-3" />
                    </button>
                  )}
                  <button onClick={clearAll} className="text-xs text-content-text-muted hover:text-content-text ml-1">
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* States */}
          {!selectedLocation && !isAllMode && (
            <div className="flex items-center justify-center py-16 text-center px-4">
              <div>
                <MapPin className="w-10 h-10 text-content-text-muted opacity-30 mx-auto mb-3" />
                <p className="text-sm text-content-text-muted">Select a location to view organizations.</p>
              </div>
            </div>
          )}
          {(selectedLocation || mode === 'all') && isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          )}
          {(selectedLocation || mode === 'all') && error && (
            <div className="flex items-center justify-center py-12 text-center px-4">
              <p className="text-sm text-content-text-muted">Error loading organizations. Please try again.</p>
            </div>
          )}
          {(selectedLocation || mode === 'all') && !isLoading && !error && filtered.length === 0 && (
            <div className="flex items-center justify-center py-16 text-center px-4">
              <div>
                <p className="text-sm text-content-text-muted font-medium">
                  {hasAnyFilter ? 'No organizations match your filters' : 'No organizations yet'}
                </p>
                {hasAnyFilter && (
                  <button onClick={clearAll} className="mt-2 text-sm text-primary hover:underline">Clear filters</button>
                )}
              </div>
            </div>
          )}


          {/* Compact detail list */}
          {(selectedLocation || mode === 'all') && !isLoading && !error && filtered.length > 0 && (
            <div>
              {filtered.map((org: any) => (
                <OrganizationDetailListItem
                  key={org.id}
                  org={org}
                  onSelect={() => handleOrgClick(org)}
                  className="px-3 hover:bg-hover"
                  descriptionLines={3}
                />
              ))}
            </div>
          )}

          {/* Infinite scroll sentinel + loading indicator (compact CA-ALL only) */}
          {isAllMode && (
            <>
              <div ref={sentinelRef} className="h-1" />
              {isLoading && page > 1 && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                </div>
              )}
              {!hasMorePages && !isLoading && accumulatedOrgs.length > 0 && (
                <p className="text-center text-xs text-content-text-muted py-4">
                  All {totalCount} organizations loaded
                </p>
              )}
            </>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // ── DESKTOP UI ─────────────────────────────────────────────────────────────
  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className="w-full flex flex-col">

        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-content-bg border-b border-border">
          <div className="px-4 py-3">
            <h1 className="text-2xl font-bold text-content-text">{pageTitle}</h1>
            {pageSubtitle && (
              <p className="text-sm text-content-text-muted mt-1">{pageSubtitle}</p>
            )}
          </div>
        </div>

        {/* Sticky Table Column Header — only for table view */}
        {viewMode === 'table' && !isLoading && !error && (
          <div className="sticky top-0 z-20 bg-sidebar-hover border-b border-border">
            <div className="px-6 py-3 space-y-3">
              <div className="text-xs font-medium text-content-text-muted uppercase tracking-wider">Organization</div>
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full max-w-sm px-3 py-2 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div>
          {/* Cards/List: search bar */}
          {viewMode !== 'table' && (
            <div className="px-4 pt-4 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-text-muted" />
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* No location selected */}
          {!selectedLocation && !isAllMode && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-content-text-muted opacity-30 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-content-text mb-2">No Location Selected</h3>
                <p className="text-content-text-muted">Please select a location to view organizations.</p>
              </div>
            </div>
          )}

          {/* Loading */}
          {(selectedLocation || isAllMode) && isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
                <p className="text-sm text-content-text-muted">Loading organizations...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {(selectedLocation || isAllMode) && error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Building2 className="w-12 h-12 text-content-text-muted opacity-30 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-content-text mb-2">Error Loading Organizations</h3>
                <p className="text-sm text-content-text-muted">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty state */}
          {(selectedLocation || isAllMode) && !isLoading && !error && filtered.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Building2 className="w-10 h-10 text-content-text-muted opacity-30 mx-auto mb-3" />
                <p className="text-content-text-muted font-medium">
                  {searchTerm ? 'No organizations match your search' : 'No organizations yet'}
                </p>
                {searchTerm && (
                  <button
                    onClick={clearAll}
                    className="mt-2 text-sm text-primary hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── TABLE VIEW ── */}
          {(selectedLocation || isAllMode) && !isLoading && !error && filtered.length > 0 && viewMode === 'table' && (
            <div className="w-full divide-y divide-border">
              {filtered.map((org: any) => (
                <div key={org.id} onClick={() => handleOrgClick(org)} className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer">
                  <span className="text-sm font-medium text-content-text">
                    {org.name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ── CARDS VIEW ── */}
          {(selectedLocation || isAllMode) && !isLoading && !error && filtered.length > 0 && viewMode === 'cards' && (
            <div className="px-4 pb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((org: any) => (
                  <div
                    key={org.id}
                    onClick={() => handleOrgClick(org)}
                    className="group bg-content-bg border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-content-text text-sm leading-snug">
                        {org.name}
                      </h3>
                      <ChevronRight className="w-4 h-4 text-content-text-muted flex-shrink-0 mt-0.5" />
                    </div>
                    {org.description && (
                      <p className="text-xs text-content-text-muted mt-2 line-clamp-2 leading-relaxed">
                        {org.description}
                      </p>
                    )}
                    {org.website && (
                      <div className="flex items-center gap-3 mt-3 text-xs text-content-text-muted">
                        <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline transition-colors" onClick={e => e.stopPropagation()}>
                          <Globe className="w-3 h-3" />
                          {((org.website.startsWith("https://") ? org.website.slice(8) : org.website.startsWith("http://") ? org.website.slice(7) : org.website).replace("//", ""))}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── DETAIL LIST VIEW ── */}
          {(selectedLocation || isAllMode) && !isLoading && !error && filtered.length > 0 && viewMode === 'list' && (
            <div className="pb-2">
              {filtered.map((org: any) => (
                <OrganizationDetailListItem
                  key={org.id}
                  org={org}
                  onSelect={() => handleOrgClick(org)}
                  className="hover:bg-sidebar-hover"
                />
              ))}
            </div>
          )}

        </div>
      </div>
    </DashboardLayout>
  );
}



